/**
 * Admin Management Page - Refactored
 * Main admin dashboard with user management, demo requests, entities, and bulk upload
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AutocompleteInput } from "../components/ui/autocomplete";
import { PhoneInput } from "../components/ui/phone-input";
import { 
  Shield, X, Check,
  GraduationCap, Hospital,
  AlertCircle, CheckCircle2
} from "lucide-react";
import { api } from "../App";

// Import refactored components
import { 
  AdminStatsCards, 
  AdminTabs, 
  UsersTab, 
  DemoRequestsTab,
  UniversitiesTab,
  OrganizationsTab,
  BulkUploadTab
} from "../components/admin";

export default function AdminManagement() {
  // Main data state
  const [users, setUsers] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [universities, setUniversities] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [clinicalSites, setClinicalSites] = useState([]);
  const [bulkUniversities, setBulkUniversities] = useState([]);
  const [bulkOrganizations, setBulkOrganizations] = useState([]);
  
  // UI state
  const [activeTab, setActiveTab] = useState("users");
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  
  // Modal states
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showUniversityModal, setShowUniversityModal] = useState(false);
  const [showOrganizationModal, setShowOrganizationModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  
  // Form state
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [convertPassword, setConvertPassword] = useState("");
  const [editingUniversity, setEditingUniversity] = useState(null);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [bulkUploadType, setBulkUploadType] = useState(null);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [selectedBulkUniversity, setSelectedBulkUniversity] = useState("");
  const [selectedBulkOrganization, setSelectedBulkOrganization] = useState("");
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  
  const [newUser, setNewUser] = useState({
    first_name: "", last_name: "", email: "", password: "", role: "university", 
    organization: "", university_id: "", organization_id: "",
    access_level: "organization", managed_site_ids: [], campus: ""
  });
  
  const [universityForm, setUniversityForm] = useState({
    name: "", address: "", city: "", state: "", zip_code: "", 
    contact_email: "", contact_phone: ""
  });
  
  const [organizationForm, setOrganizationForm] = useState({
    name: "", address: "", city: "", state: "", zip_code: "", 
    contact_email: "", contact_phone: ""
  });

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  // Fetch all data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, demosRes, statsRes, uniRes, orgRes, sitesRes, bulkUniRes, bulkOrgRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/demo-requests"),
        api.get("/admin/stats"),
        api.get("/admin/universities"),
        api.get("/admin/organizations"),
        api.get("/clinical-sites"),
        api.get("/admin/bulk-upload/universities"),
        api.get("/admin/bulk-upload/organizations")
      ]);
      setUsers(usersRes.data);
      setDemoRequests(demosRes.data);
      setStats(statsRes.data);
      setUniversities(uniRes.data);
      setOrganizations(orgRes.data);
      setClinicalSites(sitesRes.data);
      setBulkUniversities(bulkUniRes.data);
      setBulkOrganizations(bulkOrgRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
    }
  };

  // Badge color helpers
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-500/20 text-red-400";
      case "university": return "bg-cyan-500/20 text-cyan-400";
      case "clinical_site": return "bg-violet-500/20 text-violet-400";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "pending": return "bg-amber-500/20 text-amber-400";
      case "contacted": return "bg-blue-500/20 text-blue-400";
      case "converted": return "bg-green-500/20 text-green-400";
      case "rejected": return "bg-red-500/20 text-red-400";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  // User handlers
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", newUser);
      toast.success("User created successfully");
      setShowCreateUser(false);
      setNewUser({ first_name: "", last_name: "", email: "", password: "", role: "university", organization: "", university_id: "", organization_id: "", access_level: "organization", managed_site_ids: [], campus: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/users/${selectedUser.id}`, selectedUser);
      toast.success("User updated successfully");
      setShowEditUser(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Are you sure you want to delete this user?")) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("User deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleImpersonate = async (user) => {
    try {
      setImpersonating(true);
      const response = await api.post(`/admin/impersonate/${user.id}`);
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success(`Now acting as ${user.name}`);
      window.location.href = "/dashboard";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to impersonate user");
      setImpersonating(false);
    }
  };

  // Demo request handlers
  const handleUpdateDemoStatus = async (id, status) => {
    try {
      await api.put(`/admin/demo-requests/${id}?status=${status}`);
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleConvertDemo = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/demo-requests/${selectedDemo.id}/convert?password=${encodeURIComponent(convertPassword)}`);
      toast.success("Demo converted to user account");
      setShowConvertModal(false);
      setSelectedDemo(null);
      setConvertPassword("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert demo");
    }
  };

  // University handlers
  const handleOpenUniversityModal = (university = null) => {
    setEditingUniversity(university);
    setUniversityForm(university ? {
      name: university.name || "",
      address: university.address || "",
      city: university.city || "",
      state: university.state || "",
      zip_code: university.zip_code || "",
      contact_email: university.contact_email || "",
      contact_phone: university.contact_phone || ""
    } : { name: "", address: "", city: "", state: "", zip_code: "", contact_email: "", contact_phone: "" });
    setShowUniversityModal(true);
  };

  const handleSaveUniversity = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...universityForm,
        contact_email: universityForm.contact_email || null,
        contact_phone: universityForm.contact_phone || null
      };
      if (editingUniversity) {
        await api.put(`/admin/universities/${editingUniversity.id}`, payload);
        toast.success("University updated");
      } else {
        await api.post("/admin/universities", payload);
        toast.success("University created");
      }
      setShowUniversityModal(false);
      setEditingUniversity(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save university");
    }
  };

  const handleDeleteUniversity = async (id) => {
    if (!confirm("Are you sure you want to delete this university?")) return;
    try {
      await api.delete(`/admin/universities/${id}`);
      toast.success("University deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete university");
    }
  };

  // Organization handlers
  const handleOpenOrganizationModal = (organization = null) => {
    setEditingOrganization(organization);
    setOrganizationForm(organization ? {
      name: organization.name || "",
      address: organization.address || "",
      city: organization.city || "",
      state: organization.state || "",
      zip_code: organization.zip_code || "",
      contact_email: organization.contact_email || "",
      contact_phone: organization.contact_phone || ""
    } : { name: "", address: "", city: "", state: "", zip_code: "", contact_email: "", contact_phone: "" });
    setShowOrganizationModal(true);
  };

  const handleSaveOrganization = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...organizationForm,
        contact_email: organizationForm.contact_email || null,
        contact_phone: organizationForm.contact_phone || null
      };
      if (editingOrganization) {
        await api.put(`/admin/organizations/${editingOrganization.id}`, payload);
        toast.success("Organization updated");
      } else {
        await api.post("/admin/organizations", payload);
        toast.success("Organization created");
      }
      setShowOrganizationModal(false);
      setEditingOrganization(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save organization");
    }
  };

  const handleDeleteOrganization = async (id) => {
    if (!confirm("Are you sure you want to delete this organization?")) return;
    try {
      await api.delete(`/admin/organizations/${id}`);
      toast.success("Organization deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete organization");
    }
  };

  // Bulk upload handlers
  const handleDownloadTemplate = async (type) => {
    try {
      const response = await api.get(`/admin/bulk-upload/templates/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleOpenBulkUpload = (type) => {
    setBulkUploadType(type);
    setBulkUploadFile(null);
    setBulkUploadResult(null);
    setSelectedBulkUniversity("");
    setSelectedBulkOrganization("");
    setShowBulkUploadModal(true);
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      toast.error("Please select a file");
      return;
    }
    if (bulkUploadType === "students" && !selectedBulkUniversity) {
      toast.error("Please select a university");
      return;
    }
    if (bulkUploadType === "clinical-sites" && !selectedBulkOrganization) {
      toast.error("Please select an organization");
      return;
    }

    const formData = new FormData();
    formData.append("file", bulkUploadFile);

    setBulkUploading(true);
    try {
      let endpoint = `/admin/bulk-upload/${bulkUploadType}`;
      if (bulkUploadType === "students") {
        endpoint += `?university_id=${selectedBulkUniversity}`;
      } else {
        endpoint += `?organization_id=${selectedBulkOrganization}`;
      }

      const response = await api.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setBulkUploadResult(response.data);
      if (response.data.success) {
        toast.success(`Successfully imported ${response.data.imported} records`);
      } else {
        toast.warning(`Imported ${response.data.imported} records with ${response.data.failed} failures`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-500 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Management</h1>
            <p className="text-slate-400">Manage users, demo requests, and system entities</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <AdminStatsCards stats={stats} />

      {/* Tabs */}
      <AdminTabs 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
        usersCount={users.length}
        pendingDemosCount={demoRequests.filter(d => d.status === "pending").length}
        universitiesCount={universities.length}
        organizationsCount={organizations.length}
      />

      {/* Tab Content */}
      {activeTab === "users" && (
        <UsersTab 
          users={users}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          roleFilter={roleFilter}
          setRoleFilter={setRoleFilter}
          onCreateUser={() => setShowCreateUser(true)}
          onEditUser={(user) => { setSelectedUser(user); setShowEditUser(true); }}
          onDeleteUser={handleDeleteUser}
          onImpersonate={handleImpersonate}
          currentUserId={currentUser.id}
          impersonating={impersonating}
          getRoleBadgeColor={getRoleBadgeColor}
        />
      )}

      {activeTab === "demos" && (
        <DemoRequestsTab 
          demoRequests={demoRequests}
          onUpdateStatus={handleUpdateDemoStatus}
          onConvert={(demo) => { setSelectedDemo(demo); setShowConvertModal(true); }}
          getRoleBadgeColor={getRoleBadgeColor}
          getStatusBadgeColor={getStatusBadgeColor}
        />
      )}

      {activeTab === "universities" && (
        <UniversitiesTab 
          universities={universities}
          onAdd={() => handleOpenUniversityModal()}
          onEdit={handleOpenUniversityModal}
          onDelete={handleDeleteUniversity}
        />
      )}

      {activeTab === "organizations" && (
        <OrganizationsTab 
          organizations={organizations}
          onAdd={() => handleOpenOrganizationModal()}
          onEdit={handleOpenOrganizationModal}
          onDelete={handleDeleteOrganization}
        />
      )}

      {activeTab === "bulk" && (
        <BulkUploadTab 
          onDownloadTemplate={handleDownloadTemplate}
          onUpload={handleOpenBulkUpload}
        />
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-lg bg-[#151A23] border-slate-700" data-testid="create-user-modal">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Create New User</CardTitle>
                <button onClick={() => setShowCreateUser(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">First Name *</Label>
                    <Input value={newUser.first_name} onChange={(e) => setNewUser({...newUser, first_name: e.target.value})} required className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="new-user-first-name" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Last Name *</Label>
                    <Input value={newUser.last_name} onChange={(e) => setNewUser({...newUser, last_name: e.target.value})} required className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="new-user-last-name" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Email *</Label>
                    <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="new-user-email" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Password *</Label>
                    <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="new-user-password" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Role *</Label>
                  <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value, organization: "", university_id: "", organization_id: ""})} className="w-full h-10 px-3 rounded-md bg-slate-800/50 border border-slate-700 text-slate-200" data-testid="new-user-role">
                    <option value="university">University</option>
                    <option value="clinical_site">Clinical Site</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                {newUser.role === "university" && (
                  <>
                    <AutocompleteInput
                      key="university-autocomplete"
                      label="University"
                      value={newUser.university_id}
                      onChange={(id, name) => setNewUser({...newUser, university_id: id, organization: name})}
                      options={universities}
                      placeholder="Search universities..."
                      required
                      noOptionsMessage="No universities found. Create one first in the Universities tab."
                      emptyMessage="No matching universities"
                    />
                    <div className="space-y-2">
                      <Label className="text-slate-300">Campus *</Label>
                      <select 
                        value={newUser.campus} 
                        onChange={(e) => setNewUser({...newUser, campus: e.target.value})} 
                        className="w-full h-10 px-3 rounded-md bg-slate-800/50 border border-slate-700 text-slate-200"
                        required
                        data-testid="new-user-campus"
                      >
                        <option value="">Select a campus...</option>
                        <option value="San Marcos, CA">San Marcos, CA</option>
                        <option value="Dallas, TX">Dallas, TX</option>
                        <option value="Austin, TX">Austin, TX</option>
                        <option value="St. Augustine, FL">St. Augustine, FL</option>
                        <option value="Miami, FL">Miami, FL</option>
                      </select>
                    </div>
                  </>
                )}
                {newUser.role === "clinical_site" && (
                  <AutocompleteInput
                    key="organization-autocomplete"
                    label="Organization"
                    value={newUser.organization_id}
                    onChange={(id, name) => setNewUser({...newUser, organization_id: id, organization: name})}
                    options={organizations}
                    placeholder="Search organizations..."
                    required
                    noOptionsMessage="No organizations found. Create one first in the Organizations tab."
                    emptyMessage="No matching organizations"
                  />
                )}
                {newUser.role === "clinical_site" && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Access Level</Label>
                      <select value={newUser.access_level} onChange={(e) => setNewUser({...newUser, access_level: e.target.value})} className="w-full h-10 px-3 rounded-md bg-slate-800/50 border border-slate-700 text-slate-200">
                        <option value="organization">All Sites in Organization</option>
                        <option value="specific">Specific Sites Only</option>
                      </select>
                    </div>
                    {newUser.access_level === "specific" && (
                      <div className="space-y-2">
                        <Label className="text-slate-300">Managed Sites</Label>
                        <div className="max-h-40 overflow-y-auto border border-slate-700 rounded-md p-2 space-y-1">
                          {clinicalSites.map(site => (
                            <label key={site.id} className="flex items-center gap-2 p-2 hover:bg-slate-800 rounded cursor-pointer">
                              <input type="checkbox" checked={newUser.managed_site_ids?.includes(site.id)} onChange={(e) => {
                                const ids = newUser.managed_site_ids || [];
                                setNewUser({...newUser, managed_site_ids: e.target.checked ? [...ids, site.id] : ids.filter(id => id !== site.id)});
                              }} className="w-4 h-4 rounded border-slate-600" />
                              <span className="text-slate-300 text-sm">{site.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateUser(false)} className="flex-1 border-slate-700 text-slate-300">Cancel</Button>
                  <Button type="submit" className="flex-1 bg-cyan-400 text-black hover:bg-cyan-300" data-testid="submit-create-user">Create User</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-lg bg-[#151A23] border-slate-700" data-testid="edit-user-modal">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Edit User</CardTitle>
                <button onClick={() => { setShowEditUser(false); setSelectedUser(null); }} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">First Name</Label>
                    <Input value={selectedUser.first_name || ""} onChange={(e) => setSelectedUser({...selectedUser, first_name: e.target.value})} className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="edit-user-first-name" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Last Name</Label>
                    <Input value={selectedUser.last_name || ""} onChange={(e) => setSelectedUser({...selectedUser, last_name: e.target.value})} className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="edit-user-last-name" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Email</Label>
                    <Input type="email" value={selectedUser.email} onChange={(e) => setSelectedUser({...selectedUser, email: e.target.value})} className="bg-slate-800/50 border-slate-700 text-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Role</Label>
                    <select value={selectedUser.role} onChange={(e) => setSelectedUser({...selectedUser, role: e.target.value, organization: "", university_id: "", organization_id: ""})} className="w-full h-10 px-3 rounded-md bg-slate-800/50 border border-slate-700 text-slate-200">
                      <option value="university">University</option>
                      <option value="clinical_site">Clinical Site</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Status</Label>
                  <select value={selectedUser.is_active ? "active" : "inactive"} onChange={(e) => setSelectedUser({...selectedUser, is_active: e.target.value === "active"})} className="w-full h-10 px-3 rounded-md bg-slate-800/50 border border-slate-700 text-slate-200">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                {selectedUser.role === "university" && (
                  <>
                    <AutocompleteInput
                      label="University"
                      value={selectedUser.university_id || ""}
                      onChange={(id, name) => setSelectedUser({...selectedUser, university_id: id, organization: name})}
                      options={universities}
                      placeholder="Search universities..."
                      emptyMessage="No matching universities"
                    />
                    <div className="space-y-2">
                      <Label className="text-slate-300">Campus</Label>
                      <select 
                        value={selectedUser.campus || ""} 
                        onChange={(e) => setSelectedUser({...selectedUser, campus: e.target.value})} 
                        className="w-full h-10 px-3 rounded-md bg-slate-800/50 border border-slate-700 text-slate-200"
                        data-testid="edit-user-campus"
                      >
                        <option value="">Select a campus...</option>
                        <option value="San Marcos, CA">San Marcos, CA</option>
                        <option value="Dallas, TX">Dallas, TX</option>
                        <option value="Austin, TX">Austin, TX</option>
                        <option value="St. Augustine, FL">St. Augustine, FL</option>
                        <option value="Miami, FL">Miami, FL</option>
                      </select>
                    </div>
                  </>
                )}
                {selectedUser.role === "clinical_site" && (
                  <AutocompleteInput
                    label="Organization"
                    value={selectedUser.organization_id || ""}
                    onChange={(id, name) => setSelectedUser({...selectedUser, organization_id: id, organization: name})}
                    options={organizations}
                    placeholder="Search organizations..."
                    emptyMessage="No matching organizations"
                  />
                )}
                {selectedUser.role === "clinical_site" && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Access Level</Label>
                      <select value={selectedUser.access_level || "organization"} onChange={(e) => setSelectedUser({...selectedUser, access_level: e.target.value})} className="w-full h-10 px-3 rounded-md bg-slate-800/50 border border-slate-700 text-slate-200">
                        <option value="organization">All Sites in Organization</option>
                        <option value="specific">Specific Sites Only</option>
                      </select>
                    </div>
                    {selectedUser.access_level === "specific" && (
                      <div className="space-y-2">
                        <Label className="text-slate-300">Managed Sites</Label>
                        <div className="max-h-40 overflow-y-auto border border-slate-700 rounded-md p-2 space-y-1">
                          {clinicalSites.map(site => (
                            <label key={site.id} className="flex items-center gap-2 p-2 hover:bg-slate-800 rounded cursor-pointer">
                              <input type="checkbox" checked={selectedUser.managed_site_ids?.includes(site.id)} onChange={(e) => {
                                const ids = selectedUser.managed_site_ids || [];
                                setSelectedUser({...selectedUser, managed_site_ids: e.target.checked ? [...ids, site.id] : ids.filter(id => id !== site.id)});
                              }} className="w-4 h-4 rounded border-slate-600" />
                              <span className="text-slate-300 text-sm">{site.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowEditUser(false); setSelectedUser(null); }} className="flex-1 border-slate-700 text-slate-300">Cancel</Button>
                  <Button type="submit" className="flex-1 bg-cyan-400 text-black hover:bg-cyan-300" data-testid="submit-edit-user">Save Changes</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Convert Demo Modal */}
      {showConvertModal && selectedDemo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-md bg-[#151A23] border-slate-700" data-testid="convert-demo-modal">
            <CardHeader>
              <CardTitle className="text-white">Convert to User Account</CardTitle>
              <CardDescription className="text-slate-400">Create an account for {selectedDemo.name}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleConvertDemo} className="space-y-4">
                <div className="p-4 bg-slate-800/50 rounded-lg space-y-2">
                  <p className="text-sm text-slate-400">Email: <span className="text-slate-200">{selectedDemo.email}</span></p>
                  <p className="text-sm text-slate-400">Organization: <span className="text-slate-200">{selectedDemo.organization}</span></p>
                  <p className="text-sm text-slate-400">Role: <span className="text-slate-200">{selectedDemo.role.replace("_", " ")}</span></p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Set Account Password *</Label>
                  <Input type="password" value={convertPassword} onChange={(e) => setConvertPassword(e.target.value)} required placeholder="Enter password for new account" className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="convert-password" />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowConvertModal(false); setSelectedDemo(null); setConvertPassword(""); }} className="flex-1 border-slate-700 text-slate-300">Cancel</Button>
                  <Button type="submit" className="flex-1 bg-green-500 text-white hover:bg-green-400" data-testid="submit-convert"><Check className="w-4 h-4 mr-2" />Create Account</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* University Modal */}
      {showUniversityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-lg bg-[#151A23] border-slate-700" data-testid="university-modal">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <GraduationCap className="w-6 h-6 text-cyan-400" />
                  <CardTitle className="text-white">{editingUniversity ? 'Edit University' : 'Add University'}</CardTitle>
                </div>
                <button onClick={() => { setShowUniversityModal(false); setEditingUniversity(null); }} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveUniversity} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">University Name *</Label>
                  <Input value={universityForm.name} onChange={(e) => setUniversityForm({...universityForm, name: e.target.value})} required placeholder="e.g., University of California" className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="university-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Address</Label>
                    <Input value={universityForm.address} onChange={(e) => setUniversityForm({...universityForm, address: e.target.value})} placeholder="123 Main St" className="bg-slate-800/50 border-slate-700 text-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">City</Label>
                    <Input value={universityForm.city} onChange={(e) => setUniversityForm({...universityForm, city: e.target.value})} placeholder="Los Angeles" className="bg-slate-800/50 border-slate-700 text-slate-200" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">State</Label>
                    <Input value={universityForm.state} onChange={(e) => setUniversityForm({...universityForm, state: e.target.value})} placeholder="CA" className="bg-slate-800/50 border-slate-700 text-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Zip Code</Label>
                    <Input value={universityForm.zip_code} onChange={(e) => setUniversityForm({...universityForm, zip_code: e.target.value})} placeholder="90001" className="bg-slate-800/50 border-slate-700 text-slate-200" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Contact Email</Label>
                    <Input type="email" value={universityForm.contact_email} onChange={(e) => setUniversityForm({...universityForm, contact_email: e.target.value})} placeholder="contact@university.edu" className="bg-slate-800/50 border-slate-700 text-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Contact Phone</Label>
                    <PhoneInput value={universityForm.contact_phone} onChange={(e) => setUniversityForm({...universityForm, contact_phone: e.target.value})} className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="university-phone-input" />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowUniversityModal(false); setEditingUniversity(null); }} className="flex-1 border-slate-700 text-slate-300">Cancel</Button>
                  <Button type="submit" className="flex-1 bg-cyan-400 text-black hover:bg-cyan-300" data-testid="submit-university">{editingUniversity ? 'Save Changes' : 'Create University'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Organization Modal */}
      {showOrganizationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-lg bg-[#151A23] border-slate-700" data-testid="organization-modal">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Hospital className="w-6 h-6 text-violet-400" />
                  <CardTitle className="text-white">{editingOrganization ? 'Edit Organization' : 'Add Organization'}</CardTitle>
                </div>
                <button onClick={() => { setShowOrganizationModal(false); setEditingOrganization(null); }} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveOrganization} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Organization Name *</Label>
                  <Input value={organizationForm.name} onChange={(e) => setOrganizationForm({...organizationForm, name: e.target.value})} required placeholder="e.g., Kaiser Permanente" className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="organization-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Address</Label>
                    <Input value={organizationForm.address} onChange={(e) => setOrganizationForm({...organizationForm, address: e.target.value})} placeholder="123 Healthcare Blvd" className="bg-slate-800/50 border-slate-700 text-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">City</Label>
                    <Input value={organizationForm.city} onChange={(e) => setOrganizationForm({...organizationForm, city: e.target.value})} placeholder="Oakland" className="bg-slate-800/50 border-slate-700 text-slate-200" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">State</Label>
                    <Input value={organizationForm.state} onChange={(e) => setOrganizationForm({...organizationForm, state: e.target.value})} placeholder="CA" className="bg-slate-800/50 border-slate-700 text-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Zip Code</Label>
                    <Input value={organizationForm.zip_code} onChange={(e) => setOrganizationForm({...organizationForm, zip_code: e.target.value})} placeholder="94612" className="bg-slate-800/50 border-slate-700 text-slate-200" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Contact Email</Label>
                    <Input type="email" value={organizationForm.contact_email} onChange={(e) => setOrganizationForm({...organizationForm, contact_email: e.target.value})} placeholder="contact@org.com" className="bg-slate-800/50 border-slate-700 text-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Contact Phone</Label>
                    <PhoneInput value={organizationForm.contact_phone} onChange={(e) => setOrganizationForm({...organizationForm, contact_phone: e.target.value})} className="bg-slate-800/50 border-slate-700 text-slate-200" data-testid="organization-phone-input" />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowOrganizationModal(false); setEditingOrganization(null); }} className="flex-1 border-slate-700 text-slate-300">Cancel</Button>
                  <Button type="submit" className="flex-1 bg-violet-500 text-white hover:bg-violet-400" data-testid="submit-organization">{editingOrganization ? 'Save Changes' : 'Create Organization'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-lg bg-[#151A23] border-slate-700" data-testid="bulk-upload-modal">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Upload {bulkUploadType === "students" ? "Students" : "Clinical Sites"}</CardTitle>
                <button onClick={() => setShowBulkUploadModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {bulkUploadType === "students" && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Select University *</Label>
                  <select value={selectedBulkUniversity} onChange={(e) => setSelectedBulkUniversity(e.target.value)} className="w-full h-10 px-3 rounded-md bg-slate-800/50 border border-slate-700 text-slate-200" data-testid="bulk-upload-university-select">
                    <option value="">Choose a university...</option>
                    {bulkUniversities.map(uni => (
                      <option key={uni.id} value={uni.id}>{uni.name} ({uni.student_count || 0} students)</option>
                    ))}
                  </select>
                </div>
              )}
              {bulkUploadType === "clinical-sites" && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Select Organization *</Label>
                  <select value={selectedBulkOrganization} onChange={(e) => setSelectedBulkOrganization(e.target.value)} className="w-full h-10 px-3 rounded-md bg-slate-800/50 border border-slate-700 text-slate-200" data-testid="bulk-upload-organization-select">
                    <option value="">Choose an organization...</option>
                    {bulkOrganizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name} ({org.site_count || 0} sites)</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-slate-300">CSV File *</Label>
                <input type="file" accept=".csv" onChange={(e) => setBulkUploadFile(e.target.files?.[0] || null)} className="w-full text-slate-200 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-slate-700 file:text-slate-200 hover:file:bg-slate-600" data-testid="bulk-upload-file-input" />
              </div>
              {bulkUploadResult && (
                <div className={`p-4 rounded-lg ${bulkUploadResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {bulkUploadResult.success ? <CheckCircle2 className="w-5 h-5 text-green-400" /> : <AlertCircle className="w-5 h-5 text-amber-400" />}
                    <span className={bulkUploadResult.success ? 'text-green-400' : 'text-amber-400'}>Upload {bulkUploadResult.success ? 'Complete' : 'Completed with Errors'}</span>
                  </div>
                  <p className="text-sm text-slate-300">Imported: {bulkUploadResult.imported} / {bulkUploadResult.total_rows}</p>
                  {bulkUploadResult.failed > 0 && <p className="text-sm text-red-400">Failed: {bulkUploadResult.failed}</p>}
                  {bulkUploadResult.errors?.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      {bulkUploadResult.errors.slice(0, 5).map((err, i) => (
                        <p key={i} className="text-xs text-red-400">Row {err.row}: {err.error}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowBulkUploadModal(false)} className="flex-1 border-slate-700 text-slate-300" disabled={bulkUploading}>Cancel</Button>
                <Button onClick={handleBulkUpload} disabled={bulkUploading} className={`flex-1 ${bulkUploadType === "students" ? 'bg-cyan-400 text-black hover:bg-cyan-300' : 'bg-violet-500 text-white hover:bg-violet-400'} disabled:opacity-50`} data-testid="submit-bulk-upload">
                  {bulkUploading ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
